import { Routes, Route, Navigate, useSearchParams, useParams } from 'react-router-dom';

import {
  ApplyLeavePage,
  LeaveHistoryPage,
  LeavePortal,
  ManagerDashboard,
  ManagerLeaveRequestsPage,
} from '../modules/leave-management/pages';

import { EmployeeInfoPage } from '../modules/employee-info/pages/EmployeeInfoPage';

import { CustomerListPage, CustomerOnboardingPage, CustomerViewPage } from '../modules/crm/pages';
import {
  CRM_ONBOARDING_LIST_PATH,
  CRM_ONBOARDING_ADD_PATH,
  CRM_ONBOARDING_EDIT_PATH,
} from '../modules/crm/routes';

import {
  AttendanceListPage,
  MarkAttendancePage,
  RegularizationPage,
  TeamAttendancePage,
} from '../modules/attendance-management/pages';

import{
  FsmCreateTicketPage,
  FsmTicketsPage,
  FsmTicketDetailContainer,
  FsmChecklistsListPage,
  FsmChecklistTemplateFormPage,
} from '../modules/fsm/pages';

import {
  AdminHolidaysPage,
  ESSHolidaysPage,
} from '../modules/holiday-management/pages';

import { OrgTablesPage } from '../modules/org-tables/pages';

import { RolesListPage } from '../modules/roles-permissions/pages/RolesListPage';
import { CreateEditRolePage, EditRoleRoutePage } from '../modules/roles-permissions/pages/CreateEditRolePage';

import {
   TenantRegistryPage,
   CreateEditTenantPage,
   EditTenantRoutePage,
   AssignTenantAccessPage,
 } from '../modules/tenant-management/pages';

import {
  SubmoduleFeaturesPage,
  EmployeeInformationPage,
  CurrentEmploymentPage,
  ShiftMappingPage,
  PreviousEmploymentPage,
  EducationRecordsPage,
  EmployeeDocumentsPage,
  AccountDetailsPage,
  EmergencyInfoPage,
  AssetsAccessPage,
  PositionHistoryPage,
  EmployeeListPage,
  EmployeeOnboardingPage
} from '../modules/service-explorer/pages';

import { ShiftsListPage,
    CreateEditShiftPage,
    EditShiftRoutePage,
    ShiftDetailPage }
    from '../modules/organization-shifts/pages';

import { OrgChartPage, UnassignedPage } from '../modules/org-charts/pages';

import {
  PmcCostHeadsPage,
  QuotationPage,
  QuotationDetailPage,
} from '../modules/sales-quotation';

import {
  ESSAnnouncementsPage,
  AnnouncementDetailPage,
  CreatorAnnouncementsPage,
  CreatorAnnouncementDetailPage,
  HRReviewRequestsPage
} from '../modules/announcements/pages';

import { ChangePasswordPage } from '../modules/profile-settings/pages/ChangePasswordPage';
  
import {
MyPollsPage,
HRPollLogPage,
PollDashboardPage,
VotePollPage,
PollResultsPage,
ESSPollResultsPage,
} from '../modules/polls/pages';

import {
  InventoryManagementPage,
  RecordInventoryInPage,
  AllocateToCustomerPage,
  AllocationDetailPage,
  StockTransferPage,
  StockPositionDetailPage,
  ReorderAlertsPage,
} from '../modules/inventory-management';

import {
  ProductCatalog,
  ProductDetail,
  AddEditProduct,
  VendorCatalog,
  VendorDetail,
  AddEditVendor,
  WarehouseList,
  AddEditWarehouse,
  WarehouseDetail,
} from '../modules/inventory/pages';

 import {
  TicketsListPage,
  CreateEditTicketPage,
  EditTicketRoutePage,
  TicketDetailPage,
} from '../modules/ticket_management/pages';


import {
  ConveyancePolicyConfigPage,
  MyConveyanceClaimsPage,
  RatePerKmClaimPage,
  FixedMonthlyClaimPage,
  VehicleMaintenanceClaimPage,
  TeamConveyanceClaimsPage,
  AllConveyanceClaimsPage,
  ClaimApprovalDetailPage,
} from '../modules/org-local-conveyance/pages';



import { MastersConfigurationPage } from '../modules/product-metrics/MastersConfigurationPage';

import { WeeklyScheduleConfigPage } from '../modules/weekly-work-schedule/pages';

function RedirectLegacyCustomerInformation() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  if (editId) {
    return <Navigate to={CRM_ONBOARDING_EDIT_PATH(editId)} replace />;
  }
  return <Navigate to={CRM_ONBOARDING_ADD_PATH} replace />;
}

function RedirectLegacyCustomerContact() {
  const { id } = useParams();
  if (id) {
    return <Navigate to={CRM_ONBOARDING_EDIT_PATH(id)} replace />;
  }
  return <Navigate to={CRM_ONBOARDING_LIST_PATH} replace />;
}

/**
 * All pages merged in from src_other. They keep the exact same relative
 * layout (Header + Sidebar + page body) and the exact same paths they were
 * given in the original TanStack `routes/*.jsx` file-based router — just
 * mounted under `/hrms/*` here instead of at the domain root, so they can
 * live alongside the login/welcome flow that already owns `/`.
 *
 * See router/navigation.js -> ROUTE_LINKS for how the sidebar and the
 * dashboard's service/module drawer deep-link into these routes.
 */
export default function AppRoutes() {
  return (
    <Routes>
      {/* Paths are relative to parent /hrms/* — do NOT use leading slashes 
      <Route index element={<LeavePortal />} /> */}
      <Route path="hrms-team/manager" element={<ManagerDashboard />} />
      <Route path="hrms-ess/apply-leave" element={<ApplyLeavePage />} />
      <Route path="hrms-ess/leave-history" element={<LeaveHistoryPage />} />
      <Route path="hrms-team/leave-history" element={<LeaveHistoryPage />} />
      <Route path="hrms-team/leave-requests" element={<ManagerLeaveRequestsPage />} />
      <Route path="hrms-ess/attendance" element={<AttendanceListPage />} />
      <Route path="hrms-ess/attendance/mark" element={<MarkAttendancePage />} />
      <Route path="hrms-ess/attendance/regularization" element={<RegularizationPage />} />
      <Route path="hrms-ess/leave-report" element={<LeavePortal />} />
      <Route path="hrms-team/attendance" element={<TeamAttendancePage />} />
      <Route path="hrms-ess/holidays" element={<ESSHolidaysPage />} />
      <Route path="hrms-team/config/holidays" element={<AdminHolidaysPage />} />
      <Route path="hrms-team/config/org-tables" element={<OrgTablesPage />} />
      <Route path="hrms-team/config/roles-services" element={<RolesListPage />} />
      <Route path="hrms-team/config/roles-services/create" element={<CreateEditRolePage />} />
      <Route path="hrms-team/config/roles-services/edit/:id" element={<EditRoleRoutePage />} />
      <Route path="hrms-team/onboarding/employee-information" element={<EmployeeInformationPage />} />
      <Route path="hrms-team/onboarding/current-employment" element={<CurrentEmploymentPage />} />
      <Route path="hrms-team/onboarding/shift-mapping" element={<ShiftMappingPage />} />
      <Route path="hrms-team/onboarding/previous-employment" element={<PreviousEmploymentPage />} />
      <Route path="hrms-team/onboarding/education-records" element={<EducationRecordsPage />} />
      <Route path="hrms-team/onboarding/employee-documents" element={<EmployeeDocumentsPage />} />
      <Route path="hrms-team/onboarding/account-details" element={<AccountDetailsPage />} />
      <Route path="hrms-team/onboarding/emergency-info" element={<EmergencyInfoPage />} />
      <Route path="hrms-team/onboarding/assets-access" element={<AssetsAccessPage />} />
      <Route path="hrms-team/onboarding/position-history" element={<PositionHistoryPage />} />
      <Route path="hrms-ess/announcements" element={<ESSAnnouncementsPage />} />
      <Route path="hrms-ess/announcements/detail/:id" element={<AnnouncementDetailPage />} />
      <Route path="hrms-team/announcements/review" element={<HRReviewRequestsPage />} />
      <Route path="hrms-team/announcements/detail/:id" element={<CreatorAnnouncementDetailPage />} />
      <Route path="hrms-team/announcements" element={<CreatorAnnouncementsPage />} />

      <Route path="hrms-team/onboarding/employee-information" element={<EmployeeInformationPage />} />
      <Route path="hrms-team/onboarding/employees" element={<EmployeeListPage />} />
      <Route path="hrms-team/onboarding/employees/onboard" element={<EmployeeOnboardingPage />} />

      <Route path="hrms-ess/org-chart" element={<OrgChartPage />} />
      <Route path="hrms-ess/org-chart/unassigned" element={<UnassignedPage />} />
      <Route path="hrms-ess/employee-info" element={<EmployeeInfoPage showSearch={false} />} />
      <Route path="hrms-team/employee-info" element={<EmployeeInfoPage showSearch />} />
      <Route path="hrms-team/config/shifts" element={<ShiftsListPage />} />
      <Route path="hrms-team/config/shifts/create" element={<CreateEditShiftPage />} />
      <Route path="hrms-team/config/shifts/edit/:id" element={<EditShiftRoutePage />} />
      <Route path="hrms-team/config/shifts/:id" element={<ShiftDetailPage />} />

     <Route path="hrms-ess/polls" element={<PollDashboardPage />} />
     <Route path="hrms-ess/polls/vote/:id" element={<VotePollPage />} />
     <Route path="hrms-ess/polls/results/:id" element={<ESSPollResultsPage />} />
     <Route path="hrms-team/polls/log" element={<HRPollLogPage />} />
     <Route path="hrms-team/polls/results/:id" element={<PollResultsPage />} />
     <Route path="hrms-team/polls" element={<MyPollsPage />} />

     <Route path="psm/config/tenants" element={<TenantRegistryPage />} />
     <Route path="psm/config/tenants/create" element={<CreateEditTenantPage />} />
     <Route path="psm/config/tenants/edit/:id" element={<EditTenantRoutePage />} />
     <Route path="psm/config/tenants/access/:id" element={<AssignTenantAccessPage />} />

       {/* CRM Module Routes */}
      <Route path="services/CRM/Onboard Customer/Customer Assets" element={<CustomerListPage />} />
      <Route path="services/CRM/Onboard Customer/Customer Asset" element={<CustomerListPage />} />
      <Route path="crm/customers" element={<CustomerListPage />} />
      <Route path="crm/customer-information" element={<CustomerOnboardingPage />} />
      <Route path="crm/customer-contact/:id" element={<CustomerOnboardingPage />} />

      <Route path="hrms-ess/change-password" element={<ChangePasswordPage />} />
     
      <Route path="hrms-ess/polls" element={<PollDashboardPage />} />
      <Route path="hrms-ess/polls/vote/:id" element={<VotePollPage />} />
      <Route path="hrms-ess/polls/results/:id" element={<ESSPollResultsPage />} />
      <Route path="hrms-team/polls/log" element={<HRPollLogPage />} />
      <Route path="hrms-team/polls/results/:id" element={<PollResultsPage />} />
      <Route path="hrms-team/polls" element={<MyPollsPage />} />

      <Route path="fsm/create-customer-complaint" element={<FsmCreateTicketPage/>} />
      <Route path="fsm/customer-complaints/:id" element={<FsmTicketDetailContainer/>} />
      <Route path="fsm/customer-complaints" element={<FsmTicketsPage/>} />
      <Route path="fsm/complaints-checklist/add" element={<FsmChecklistTemplateFormPage/>} />
      <Route path="fsm/complaints-checklist/update/:id" element={<FsmChecklistTemplateFormPage/>} />
      <Route path="fsm/complaints-checklist" element={<FsmChecklistsListPage/>} />

      {/* CRM Module Routes — Home → CRM → Customer → Onboarding */}
      <Route path="services/CRM/Customer/Onboarding" element={<CustomerListPage />} />
      <Route path="services/CRM/Customer/Onboarding/add" element={<CustomerOnboardingPage />} />
      <Route path="services/CRM/Customer/Onboarding/edit/:customerId" element={<CustomerOnboardingPage />} />
      <Route path="services/CRM/Customer/Onboarding/view/:customerId" element={<CustomerViewPage />} />

      {/* Legacy CRM path redirects */}
      <Route path="services/CRM/Onboard Customer/Customer Assets" element={<Navigate to={CRM_ONBOARDING_LIST_PATH} replace />} />
      <Route path="services/CRM/Onboard Customer/Customer Asset" element={<Navigate to={CRM_ONBOARDING_LIST_PATH} replace />} />
      <Route path="crm/customers" element={<Navigate to={CRM_ONBOARDING_LIST_PATH} replace />} />
      <Route path="crm/customer-information" element={<RedirectLegacyCustomerInformation />} />
      <Route path="crm/customer-contact/:id" element={<RedirectLegacyCustomerContact />} />
      <Route path="hrms-team/config/weekly-work-schedule" element={<WeeklyScheduleConfigPage />} />
      <Route path="inventory-management" element={<Navigate to="/inventory-management/stock-position" replace />} />
      <Route path="inventory-management/stock-position" element={<InventoryManagementPage />} />
      <Route path="inventory-management/stock-position/:productId" element={<StockPositionDetailPage />} />
      <Route path="inventory-management/record-inventory-in" element={<RecordInventoryInPage />} />
      <Route path="inventory-management/allocate-to-customer" element={<AllocateToCustomerPage />} />
      <Route path="inventory-management/allocation-detail/:allocationId" element={<AllocationDetailPage />} />
      <Route path="inventory-management/stock-transfer" element={<StockTransferPage />} />
      <Route path="inventory-management/reorder-alerts" element={<ReorderAlertsPage />} />
      <Route path="product-metrics" element={<MastersConfigurationPage />} />

      {/* IMS → Inventory Management → Product Catalogue / Product Vendor / Inventory Location */}
      <Route path="IMS/inventory/products" element={<ProductCatalog />} />
      <Route path="IMS/inventory/products/new" element={<AddEditProduct />} />
      <Route path="IMS/inventory/products/:productId/edit" element={<AddEditProduct />} />
      <Route path="IMS/inventory/products/:productId" element={<ProductDetail />} />
      <Route path="IMS/inventory/vendors" element={<VendorCatalog />} />
      <Route path="IMS/inventory/vendors/new" element={<AddEditVendor />} />
      <Route path="IMS/inventory/vendors/:vendorId/edit" element={<AddEditVendor />} />
      <Route path="IMS/inventory/vendors/:vendorId" element={<VendorDetail />} />
      <Route path="IMS/inventory/locations" element={<WarehouseList />} />
      <Route path="IMS/inventory/locations/new" element={<AddEditWarehouse />} />
      <Route path="IMS/inventory/locations/:id/edit" element={<AddEditWarehouse />} />
      <Route path="IMS/inventory/locations/:id" element={<WarehouseDetail />} />

      <Route path="sales/configurations/pmc-cost-heads" element={<PmcCostHeadsPage />} />
      <Route path="sales/configurations/approval-rules" element={<PmcCostHeadsPage />} />
      <Route path="sales/configurations/net-price-configuration" element={<PmcCostHeadsPage />} />
      <Route path="sales/configurations/terms-library" element={<PmcCostHeadsPage />} />
      <Route path="sales/quotations/quotation" element={<QuotationPage />} />
      <Route path="sales/quotations/quotation/:refId" element={<QuotationDetailPage />} />

      <Route path="hrms-ess/conveyance-claims" element={<MyConveyanceClaimsPage />} />
      <Route path="hrms-ess/conveyance-claims/rate-per-km/new" element={<RatePerKmClaimPage />} />
      <Route path="hrms-ess/conveyance-claims/rate-per-km/:claimId" element={<RatePerKmClaimPage />} />
      <Route path="hrms-ess/conveyance-claims/fixed-monthly/new" element={<FixedMonthlyClaimPage />} />
      <Route path="hrms-ess/conveyance-claims/fixed-monthly/:claimId" element={<FixedMonthlyClaimPage />} />
      <Route path="hrms-ess/conveyance-claims/maintenance/new" element={<VehicleMaintenanceClaimPage />} />
      <Route path="hrms-ess/conveyance-claims/maintenance/:claimId" element={<VehicleMaintenanceClaimPage />} />

      <Route path="hrms-team/conveyance-claims" element={<TeamConveyanceClaimsPage />} />
      <Route path="hrms-team/conveyance-claims/all" element={<AllConveyanceClaimsPage />} />
      <Route path="hrms-team/conveyance-claims/:claimId" element={<ClaimApprovalDetailPage />} />
      <Route path="hrms-team/config/expense-policy" element={<ConveyancePolicyConfigPage />} />

      <Route path="psm/tickets" element={<TicketsListPage />} />
      <Route path="psm/tickets/create" element={<CreateEditTicketPage />} />
      <Route path="psm/tickets/:requestId/edit" element={<EditTicketRoutePage />} />
      <Route path="psm/tickets/:requestId" element={<TicketDetailPage />} />

      {/* Generic fallback for any enabledServices submodule that has no
          dedicated page yet (see router/navigation.js). */}
      <Route path="services/:service/:module/:submodule" element={<SubmoduleFeaturesPage />} />
      {/*<Route path="*" element={<Navigate to="/hrms" replace />*/}
      <Route path="*" element={<Navigate to="/" replace />
    } />
    </Routes>
  );
}
